import { getReceptionReportTotals } from '@/pages/reception/services/reception.services';
import { fetchWorkedHours } from '@/pages/workedHours/services/workedHours.services';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services.jsx';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Minimal helper to query users by role for a lavanderia
export const fetchUsersByRole = async ({ lavanderiaId, lavanderiaIds, roles = [], estado = 'Activo', limit = 1000, offset = 1 }) => {
  const ids = Array.isArray(lavanderiaIds) && lavanderiaIds.length ? lavanderiaIds : (lavanderiaId ? [lavanderiaId] : []);
  const body = {
    limit,
    offset,
    query: {
      estado,
      ...(ids.length ? { 'lavanderia.id': ids.length > 1 ? { $in: ids } : ids[0] } : {}),
      ...(roles.length ? { rol: { $in: roles } } : {}),
    },
  };
  const resp = await fetch(`${API_URL}/user/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.message || 'Error al cargar usuarios');
  }
  const data = await resp.json();
  // Backend returns { data, totalRecords }
  return data?.data || [];
};

// Calculates invoice items based on business rules
export const calculateBilling = async ({ lavanderiaId, lavanderiaIds, from, to }) => {
  const ids = Array.isArray(lavanderiaIds) && lavanderiaIds.length ? lavanderiaIds : (lavanderiaId ? [lavanderiaId] : []);
  if (!ids.length || !from || !to) throw new Error('Parámetros insuficientes');

  const dateRangeReception = { createdAt: { $gte: from, $lte: to } };
  const dateRangeWorked = { fecha: { $gte: from, $lte: to } };

  // 1) Receptions: sum kilosProcesados and breakdown per lavanderia
  let kilosProcesados = 0;
  let receptionsByLaundry = [];
  try {
    const results = [];
    for (const lid of ids) {
      try {
        const r = await getReceptionReportTotals({ query: { 'lavanderia.id': lid, ...dateRangeReception } });
        const k = Number(r?.data?.kilosProcesados || 0);
        results.push({ lavanderiaId: lid, kilos: k });
      } catch {
        results.push({ lavanderiaId: lid, kilos: 0 });
      }
    }
    // Fetch laundry names in a single call and enrich results
    try {
      const laundriesResp = await fetchLaundries({ limit: ids.length, offset: 1, query: { _id: { $in: ids } } });
      const list = Array.isArray(laundriesResp?.data) ? laundriesResp.data : [];
      const nameById = new Map(list.map(l => [String(l._id || l.id), l.nombre || l.name || '']));
      receptionsByLaundry = results.map(it => ({ ...it, nombre: nameById.get(String(it.lavanderiaId)) || String(it.lavanderiaId) }));
    } catch {
      receptionsByLaundry = results;
    }
    kilosProcesados = results.reduce((s, it) => s + Number(it.kilos || 0), 0);
  } catch { kilosProcesados = 0; receptionsByLaundry = []; }

  // 2) Worked hours: distinct employees who used clock in/out (prefer with horaSalida)
  let uniqueUsersCount = 0;
  let workedUsers = [];
  try {
    const result = await fetchWorkedHours({
      limit: 5000,
      page: 1,
      query: { 'lavanderia.id': ids.length > 1 ? { $in: ids } : ids[0], ...dateRangeWorked, horaSalida: { $exists: true, $ne: null } },
    });
    const rows = Array.isArray(result?.data) ? result.data : [];
    const map = new Map();
    for (const r of rows) {
      const id = r?.empleado?.id || r?.empleado?._id;
      const nombre = r?.empleado?.nombre || r?.empleado?.name || '';
      const cedula = r?.empleado?.cedula || '';
      const rol = r?.empleado?.rol || '';
      if (!id) continue;
      const key = String(id);
      const prev = map.get(key);
      if (!prev) {
        map.set(key, { id: key, nombre, cedula, rol, count: 1 });
      } else {
        prev.count = (prev.count || 0) + 1;
        map.set(key, prev);
      }
    }
    workedUsers = Array.from(map.values());
    uniqueUsersCount = workedUsers.length;
  } catch {
    uniqueUsersCount = 0;
    workedUsers = [];
  }

  // 3) Users by role (admin/supervisor)
  let admins = 0;
  let supervisors = 0;
  let adminUsers = [];
  let supervisorUsers = [];
  try {
    const users = await fetchUsersByRole({ lavanderiaIds: ids, roles: ['admin', 'supervisor'], estado: 'Activo' });
    adminUsers = users.filter(u => String(u?.rol).toLowerCase() === 'admin').map(u => ({ id: u._id || u.id, nombre: u.nombre, correo: u.correo }));
    supervisorUsers = users.filter(u => String(u?.rol).toLowerCase() === 'supervisor').map(u => ({ id: u._id || u.id, nombre: u.nombre, correo: u.correo }));
    admins = adminUsers.length;
    supervisors = supervisorUsers.length;
  } catch { /* keep zero */ }

  // Pricing
  const PRICE_WORKED_USER = 5000;
  const PRICE_RECEPTION_KG = 5;
  const PRICE_ROLE = 10000; // admin/supervisor

  const items = [
    {
      code: 'WORKED_HOURS_USERS',
      description: 'Usuarios que registraron horas (con salida)',
      quantity: uniqueUsersCount,
      unitPrice: PRICE_WORKED_USER,
      amount: uniqueUsersCount * PRICE_WORKED_USER,
    },
    {
      code: 'RECEPTIONS_KG',
      description: 'Kilos recepcionados',
      quantity: Number(kilosProcesados.toFixed(2)),
      unitPrice: PRICE_RECEPTION_KG,
      amount: Number((kilosProcesados * PRICE_RECEPTION_KG).toFixed(2)),
    },
    {
      code: 'USERS_ADMIN',
      description: 'Usuarios admin activos',
      quantity: admins,
      unitPrice: PRICE_ROLE,
      amount: admins * PRICE_ROLE,
    },
    {
      code: 'USERS_SUPERVISOR',
      description: 'Usuarios supervisor activos',
      quantity: supervisors,
      unitPrice: PRICE_ROLE,
      amount: supervisors * PRICE_ROLE,
    },
  ];

  const subtotal = items.reduce((s, it) => s + Number(it.amount || 0), 0);
  const taxes = { iva: 0 };
  const total = subtotal + taxes.iva;

  // Build id->nombre helper for UI convenience
  const laundryNameMap = receptionsByLaundry.reduce((acc, it) => {
    if (it?.lavanderiaId && it?.nombre) acc[String(it.lavanderiaId)] = it.nombre;
    return acc;
  }, {});

  return {
    period: { from, to },
    items,
    subtotal,
    taxes,
    total,
    meta: { lavanderiaIds: ids },
    details: {
      workedUsers,
      adminUsers,
      supervisorUsers,
      receptionsByLaundry,
      laundryNameMap,
    },
  };
};
